import React from 'react';
import { Box, Typography } from '@mui/material';

import type { CardSelectorOption } from '../../../../components/core';
import { CardSelector } from '../../../../components/core';
const withDisabledAfterFirst = (
  options: readonly Omit<CardSelectorOption, 'disabled'>[],
): CardSelectorOption[] =>
  options.map((option, index) => ({
    ...option,
    disabled: index > 0,
  }));

export const AGENT_TEMPLATE_OPTIONS = withDisabledAfterFirst([
  {
    id: 'commerce-agent',
    title: 'Commerce Agent',
    description:
      'Product discovery, add-to-cart, checkout assistance, and order questions for online shoppers.',
  },
  {
    id: 'support-agent',
    title: 'Support Agent',
    description:
      'Post-purchase help — returns, shipping status, policy questions, and account issues.',
  },
  {
    id: 'service-agent',
    title: 'Service agent',
    description: 'Service appointments, bookings, and customer service requests.',
  },
  {
    id: 'demo-agent',
    title: 'Demo agent',
    description: 'Showcase capabilities with a pre-configured demo experience.',
  },
  {
    id: 'brand-voice',
    title: 'Brand Voice',
    description: 'Content creation, brand messaging, and marketing copy assistance.',
  },
]);

export const INDUSTRY_VERTICAL_OPTIONS = withDisabledAfterFirst([
  {
    id: 'retail',
    title: 'Retail',
    description: 'Online and in-store retail: catalog browse, promotions, cart, and checkout.',
  },
  {
    id: 'luxury',
    title: 'Luxury',
    description: 'High-touch shopping for premium brands and exclusive collections.',
  },
  {
    id: 'travel',
    title: 'Travel',
    description: 'Trip planning, bookings, itinerary changes, and travel support.',
  },
  {
    id: 'fitness',
    title: 'Fitness',
    description: 'Memberships, classes, gear recommendations, and training support.',
  },
  {
    id: 'beauty-skincare',
    title: 'Beauty & skincare',
    description: 'Product recommendations, routines, and beauty consultation.',
  },
  {
    id: 'home-furniture',
    title: 'Home & furniture',
    description: 'Room planning, product fit, delivery questions, and order support.',
  },
]);

export const DEFAULT_AGENT_TEMPLATE_ID = AGENT_TEMPLATE_OPTIONS[0].id;
export const DEFAULT_INDUSTRY_VERTICAL_ID = INDUSTRY_VERTICAL_OPTIONS[0].id;

export const TemplateStep = (): JSX.Element => (
  <>
    <Typography
      variant="small"
      color="text.secondary"
      data-testid="ella-onboarding-template-api-note"
      sx={{ mb: 3, display: 'block' }}
    >
      Template and industry vertical are not supported by the API yet. The first option is locked
      for this onboarding flow — there is no backend hook-up on publish.
    </Typography>

    <CardSelector
      name="Agent template"
      sectionTitle="Agent template"
      sectionDescription="Choose the primary role this agent will play for shoppers."
      options={AGENT_TEMPLATE_OPTIONS}
      value={DEFAULT_AGENT_TEMPLATE_ID}
      onChange={() => undefined}
      dataTestId="ella-onboarding-agent-template"
      columns={{ xs: 1, sm: 2, md: 2, lg: 4 }}
    />

    <Box sx={{ mt: 4 }}>
      <CardSelector
        name="Industry vertical"
        sectionTitle="Industry vertical"
        sectionDescription="Choose the vertical that best matches how this merchant sells."
        options={INDUSTRY_VERTICAL_OPTIONS}
        value={DEFAULT_INDUSTRY_VERTICAL_ID}
        onChange={() => undefined}
        dataTestId="ella-onboarding-industry-vertical"
        columns={{ xs: 1, sm: 2, md: 3, lg: 3 }}
      />
    </Box>
  </>
);
